import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  ArrowBigUp, BadgeCheck, Check, ExternalLink, ImagePlus, Lock, MessageCircle, Play, Scissors, TriangleAlert,
} from "lucide-react";
import {
  AddToCalendar, Avatar, Btn, Card, Chip, CopyCode, CountdownBoxes, Empty, Hand, JoinButton, LocalTime, Name, P, PLANS, PLink, lockLine,
  Polaroid, PortalShell, ProgressBar, ProgressRing, Section, Segmented, SessionChip, Sheet, Thumb, TierLock,
  useChecklist, useCountdown, usePortal,
  CHELSEA, ME, ago, fmtDay, fmtDur, fmtLeft, fmtT, getProject, getSession, hasReplay, makesAt, postsFor, projectRuntime,
  qaFor, sessionState, sessionsFor,
  type Item, type Make, type Project, type Question, type Session, type TapeColor,
} from "./Portal";
import { KIND_ONE, PostSheet, nextToWatch, projectStatus, useWide, type WatchPick } from "./Classes";
import "./Project.css";

/* Project page (P2 "Project room", research §5).
   One page per monthly project, in this order: hero + status, "Read before
   you start" fixes, What you need (pattern, code, supply checklist), Sessions
   (Prep → Class 1 → Class 2), makes as taped polaroids, and Q&A with
   Chelsea's answers marked.
   Phone: one scroll with a sticky section-chip bar. Desktop: content on the
   left, a sticky rail with the next session and the supply checklist.
   Thread Friends, free accounts and visitors get the same page with
   tier-named locks (and a 30-second preview link) instead of replays.
   Prototype: the checklist is saved locally; questions, votes and makes are
   local state only. Nothing is sent anywhere. */

const TAPES: TapeColor[] = ["pink", "yellow", "green", "peri"];
const TILTS = [-2.5, 1.8, -1.2, 2.4, -1.8, 1.2];

/* ═══════════════════════════ Small pieces ═══════════════════════════ */

function watchLabel(w: WatchPick) {
  const x = w.session, len = x.replay!.length;
  if (w.kind === "resume") return { label: `Resume ${x.label}`, meta: fmtLeft(len, x.progress) };
  if (w.kind === "rewatch") return { label: `Rewatch ${x.label}`, meta: fmtDur(len) };
  return { label: `Watch ${x.label}`, meta: fmtDur(len) };
}

function Starts({ at, prefix = "Starts in" }: { at: Date; prefix?: string }) {
  const c = useCountdown(at);
  return <span className="pj-starts">{prefix} {c.label}</span>;
}

/** Status line for the hero / rail. */
function StatusLine({ p }: { p: Project }) {
  const { now, tz } = usePortal();
  const st = projectStatus(p, now, tz);
  return <div className="pj-status">
    <Chip tone={st.tone}>{st.label}</Chip>
    {st.session && (st.state === "tonight" || st.state === "upcoming") && <LocalTime date={st.session.start} relative={st.state === "tonight"} withDay={st.state !== "tonight" && !st.label.startsWith("Starts")} className="pj-status-time" />}
    {st.state === "processing" && <span className="pj-status-time">Replay up by morning</span>}
  </div>;
}

/* ═══════════════════════════ Hero ═══════════════════════════ */

function Hero({ p }: { p: Project }) {
  const { now, tz, can, role, to } = usePortal();
  const nav = useNavigate();
  const st = projectStatus(p, now, tz);
  const r = projectRuntime(p, now);
  const pick = nextToWatch(p, now);
  const member = can("replays");
  const firstReplay = sessionsFor(p.id).find(x => x.replay && hasReplay(x, now) && !x.recorded) ?? sessionsFor(p.id).find(x => x.replay && hasReplay(x, now));
  const hand = p.status === "current" ? "this month's project!" : p.status === "announced" ? "coming soon!" : undefined;
  const ss = sessionsFor(p.id);
  const classes = ss.filter(x => x.label !== "Prep");
  return <Card pad="none" className="pj-hero" tape={p.status === "past" ? undefined : "yellow"} tapeSide="left" as="section">
    <div className="pj-hero-img">
      <img src={p.hero} alt={p.heroAlt} />
      {hand && <Hand className="pj-hero-hand">{hand}</Hand>}
    </div>
    <div className="pj-hero-body">
      <p className="pt-kicker">{p.monthLabel} · {KIND_ONE[p.kind]} · {p.level}</p>
      <h1 className="pj-hero-title">{p.title}</h1>
      <p className="pj-hero-sub">{p.subtitle} · {p.pattern.designer.startsWith("Chelsea") ? p.pattern.designer : `pattern by ${p.pattern.designer}`}</p>
      <StatusLine p={p} />
      <p className="pj-hero-teaser">{p.teaser}</p>
      <p className="pj-hero-facts">
        {ss.some(x => x.label === "Prep") ? "Prep video + " : ""}{classes.length} live classes
        {classes[0] && ` · ${fmtDay(classes[0].start, "America/Denver", { month: "short", day: "numeric" })}${classes[1] ? ` & ${fmtDay(classes[1].start, "America/Denver", { day: "numeric" })}` : ""}`}
        {r.total > 0 && ` · ${fmtDur(r.total)} of replays`}
      </p>
      <div className="pj-hero-act">
        {member ? <>
          {st.state === "live" && st.session && <Btn variant="live" size="lg" to={P.session(st.session.id)} icon={<Play size={18} fill="currentColor" aria-hidden="true" />}>Join live</Btn>}
          {pick && (() => { const w = watchLabel(pick); return <Btn variant={st.state === "live" ? "secondary" : "primary"} size={st.state === "live" ? "md" : "lg"} to={P.session(pick.session.id)} icon={<Play size={18} aria-hidden="true" />}>
            {w.label}<span className="pj-btn-meta">{w.meta}</span>
          </Btn>; })()}
          {st.session && (st.state === "tonight" || st.state === "upcoming") && <AddToCalendar session={st.session} variant="secondary" size={pick ? "md" : "lg"} />}
        </> : <>
          <JoinButton need="member" size="lg" />
          {firstReplay && <Btn variant="ghost" icon={<Play size={16} aria-hidden="true" />} onClick={() => nav(to(`${P.session(firstReplay.id)}?preview=1`))}>Watch 30s preview</Btn>}
          {role === "visitor" && <PLink to={P.me} className="pj-signin">Already a member? Sign in</PLink>}
        </>}
      </div>
      {member && r.total > 0 && <div className="pj-hero-progress">
        <ProgressRing value={r.ratio} size={40} showPct />
        <span><b>{Math.round(r.ratio * 100)}% watched</b><small>{fmtDur(r.watched)} of {fmtDur(r.total)}</small></span>
      </div>}
    </div>
  </Card>;
}

/* ═══════════════════════════ Section chip bar ═══════════════════════════ */

type SecId = "overview" | "supplies" | "classes" | "makes" | "qa";
const SECS: { id: SecId; label: string }[] = [
  { id: "overview", label: "Overview" }, { id: "supplies", label: "Supplies" }, { id: "classes", label: "Classes" }, { id: "makes", label: "Makes" }, { id: "qa", label: "Q&A" },
];
function ChipBar({ wide }: { wide: boolean }) {
  const secs = wide ? SECS.filter(s => s.id !== "supplies") : SECS;
  const [on, setOn] = useState<SecId>("overview");
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    let raf = 0;
    const calc = () => {
      raf = 0;
      const line = (barRef.current?.getBoundingClientRect().bottom ?? 120) + 24;
      let cur: SecId = secs[0].id;
      for (const s of secs) {
        const el = document.getElementById(`pj-${s.id}`);
        if (el && el.getBoundingClientRect().top <= line) cur = s.id;
      }
      // At the very bottom, the last section wins even if it's short.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) cur = secs[secs.length - 1].id;
      setOn(cur);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(calc); };
    calc();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [wide]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const b = barRef.current?.querySelector<HTMLElement>(`[data-sec="${on}"]`);
    b?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [on]);
  const go = (id: SecId) => {
    setOn(id);
    document.getElementById(`pj-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return <nav className="pj-bar" aria-label="On this page" ref={barRef}>
    {secs.map(s => <button key={s.id} type="button" data-sec={s.id} className={on === s.id ? "is-on" : ""} aria-current={on === s.id ? "true" : undefined} onClick={() => go(s.id)}>{s.label}</button>)}
  </nav>;
}

/* ═══════════════════════════ Overview ═══════════════════════════ */

function Errata({ p }: { p: Project }) {
  const { now, can } = usePortal();
  const list = p.errata.filter(e => { const x = getSession(e.sessionId); return !x || hasReplay(x, now); });
  if (!list.length || !can("supplies")) return null;
  return <div className="pj-errata" role="note" aria-labelledby="pj-errata-h">
    <span className="pj-errata-icon"><TriangleAlert size={20} aria-hidden="true" /></span>
    <div className="pj-errata-main">
      <p className="pj-errata-h" id="pj-errata-h">Read before you start</p>
      <ul>
        {list.map(e => { const x = getSession(e.sessionId); return <li key={e.id}>
          <span>{e.text}</span>
          {x && e.t != null && (can("replays")
            ? <PLink to={`${P.session(x.id)}?at=${e.t}`} className="pj-ts"><Play size={12} fill="currentColor" aria-hidden="true" />{x.label} · {fmtT(e.t)}</PLink>
            : <span className="pj-ts is-off">{x.label} · {fmtT(e.t)}</span>)}
        </li>; })}
      </ul>
    </div>
  </div>;
}

function Overview({ p }: { p: Project }) {
  const posts = postsFor(p.id);
  const { role } = usePortal();
  const [open, setOpen] = useState<string | null>(null);
  const rank = { visitor: 0, free: 1, friend: 2, member: 3, chelsea: 4 } as const;
  return <section id="pj-overview" className="pj-sec" aria-label="Overview">
    <Errata p={p} />
    <Card className="pj-about">
      <div className="pj-about-head"><Avatar person={CHELSEA} size={40} /><span><Name person={CHELSEA} /><small>About this project</small></span></div>
      {p.about.map((t, i) => <p key={i}>{t}</p>)}
    </Card>
    {posts.length > 0 && <div className="pj-posts">
      <p className="pt-kicker">From the studio</p>
      <ul>
        {posts.map(x => <li key={x.id}>
          <button type="button" onClick={() => setOpen(x.id)}>
            <Thumb src={x.cover ?? "/img/chelsea.jpeg"} ratio="1/1" width={52} />
            <span><b>{x.title}</b><small>{x.minutes} min read{rank[role] < rank[x.need] ? ` · ${x.need === "member" ? PLANS.member.tag : PLANS.friend.tag}` : ""}</small></span>
            {rank[role] < rank[x.need] && <Lock size={15} aria-label="Locked" className="pj-posts-lock" />}
          </button>
        </li>)}
      </ul>
    </div>}
    <PostSheet post={posts.find(x => x.id === open) ?? null} onClose={() => setOpen(null)} />
  </section>;
}

/* ═══════════════════════════ What you need ═══════════════════════════ */

function SupplyRow({ it, on, toggle }: { it: Item; on: boolean; toggle: () => void }) {
  return <li>
    <label className={`pj-item${on ? " is-on" : ""}`}>
      <input type="checkbox" checked={on} onChange={toggle} />
      <span className="pj-box" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
      <span className="pj-item-main">
        <span className="pj-item-name">{it.name}{it.optional && <span className="pj-opt">optional</span>}</span>
        {(it.qty || it.note) && <span className="pj-item-meta">{[it.qty, it.note].filter(Boolean).join(" · ")}</span>}
      </span>
    </label>
  </li>;
}

function Supplies({ p, compact }: { p: Project; compact?: boolean }) {
  const { can, now, role } = usePortal();
  const ck = useChecklist(p.id);
  const [list, setList] = useState<"pattern" | "chelseaUsed">("pattern");
  const items = p.supplies[list];
  const c = ck.count(p.supplies.pattern);
  const expired = p.pattern.codeExpires && p.pattern.codeExpires < now;
  const head = <div className="pj-need-head">
    <h2 className="pj-need-title"><Scissors size={18} aria-hidden="true" /> What you need</h2>
    {can("supplies") && p.supplies.pattern.length > 0 && <span className="pj-need-count">{c.done} of {c.total} gathered</span>}
  </div>;

  if (!can("supplies")) return <Card className="pj-need" as="section">
    {head}
    <TierLock thing="supplies" force title="Pattern, code & supply list" body="Create a free account to see the supply list and tick things off as you gather them. The pattern discount code is for Members." />
  </Card>;

  return <Card className={`pj-need${compact ? " is-compact" : ""}`} as="section">
    {head}
    {p.supplies.pattern.length > 0 && <ProgressBar value={c.total ? c.done / c.total : 0} label="Supplies gathered" />}

    <div className="pj-pattern">
      <div className="pj-pattern-main">
        <p className="pj-pattern-name">{p.pattern.name}</p>
        <p className="pj-pattern-meta">{p.pattern.designer} · {p.pattern.price}</p>
      </div>
      {p.pattern.url !== "#" && <Btn variant="secondary" size="sm" href={p.pattern.url} iconEnd={<ExternalLink size={14} aria-hidden="true" />}>Get pattern</Btn>}
    </div>
    {p.pattern.code && !expired && <CopyCode code={p.pattern.code} discount={p.pattern.discount} expires={p.pattern.codeExpires} block />}
    {p.pattern.code && expired && <p className="pj-expired">Code {can("code") ? p.pattern.code : ""} ({p.pattern.discount}) ended {fmtDay(new Date(p.pattern.codeExpires!.getTime() - 6e4), "America/Denver", { month: "short", day: "numeric" })}.</p>}
    {p.pattern.free && !/free/i.test(p.pattern.price) && <p className="pj-expired">The pattern is free.</p>}
    {p.kitUrl && <Btn variant="quiet" size="sm" href={p.kitUrl}>Fabric kit</Btn>}

    {p.supplies.pattern.length === 0
      ? <Empty title="Supply list coming soon" hand="start saving scraps!" body={`Chelsea posts the list and the pattern the first week of ${p.monthLabel.split(" ")[0]}. We'll send a note.`} />
      : <>
        <Segmented value={list} onChange={setList} block size="sm" label="Supply list" options={[
          { value: "pattern", label: "Pattern list", count: p.supplies.pattern.length },
          ...(p.supplies.chelseaUsed.length ? [{ value: "chelseaUsed" as const, label: "Chelsea used", count: p.supplies.chelseaUsed.length }] : []),
        ]} />
        {list === "chelseaUsed" && <p className="pj-need-note">What Chelsea actually used. Swap in anything you like.</p>}
        <ul className="pj-items" aria-label={list === "pattern" ? "Pattern list" : "What Chelsea used"}>
          {items.map(it => <SupplyRow key={it.id} it={it} on={ck.has(it.id)} toggle={() => ck.toggle(it.id)} />)}
        </ul>
        <p className="pj-need-foot">{role === "visitor" ? "" : "Saved on this device."}</p>
      </>}
  </Card>;
}

/* ═══════════════════════════ Sessions ═══════════════════════════ */

function SessionItem({ s }: { s: Session }) {
  const { now, can } = usePortal();
  const state = sessionState(s, now);
  const member = can("replays");
  const rep = s.replay && state === "replay" ? s.replay : undefined;
  const prog = rep ? Math.min(1, (s.progress ?? 0) / rep.length) : 0;
  const done = rep && prog >= 0.98;
  return <li className={`pj-sess is-${state}`}>
    <PLink to={P.session(s.id)} className="pj-sess-media" aria-label={`${s.label}: open`} tabIndex={-1}>
      {rep
        ? <Thumb src={rep.poster} duration={rep.length} progress={member ? prog : undefined} play={member} locked={!member} />
        : <span className="pj-sess-date">
            <small>{fmtDay(s.start, "America/Denver", { weekday: "short" })}</small>
            <b>{fmtDay(s.start, "America/Denver", { month: "short", day: "numeric" })}</b>
            {state === "live" && <Chip tone="live">Live</Chip>}
          </span>}
    </PLink>
    <div className="pj-sess-main">
      <div className="pj-sess-top">
        <PLink to={P.session(s.id)} className="pj-sess-title">{s.label === "Prep" ? "Prep video" : s.label}</PLink>
        <SessionChip state={state} />
        {member && done && <Chip tone="ok" icon={<Check size={13} aria-hidden="true" />}>Watched</Chip>}
        {rep && rep.fixNotes.length > 0 && <Chip tone="gold" icon={<TriangleAlert size={12} aria-hidden="true" />}>{rep.fixNotes.length} fix {rep.fixNotes.length === 1 ? "note" : "notes"}</Chip>}
      </div>
      <p className="pj-sess-meta">
        {s.recorded ? `${state === "replay" ? "Posted" : "Video posts"} ${fmtDay(s.start, "America/Denver", { month: "short", day: "numeric" })}` : rep ? `Live ${fmtDay(s.start, "America/Denver", { month: "short", day: "numeric" })}` : <LocalTime date={s.start} withDay />}
        {rep ? ` · ${fmtDur(rep.length)}${rep.chapters.length ? ` · ${rep.chapters.length} chapters` : ""}` : !s.recorded ? ` · about ${fmtDur(s.minutes * 60)}` : ""}
      </p>
      {s.blurb && <p className="pj-sess-blurb">{s.blurb}</p>}

      <div className="pj-sess-act">
        {member ? <>
          {state === "live" && <Btn variant="live" size="sm" to={P.session(s.id)} icon={<Play size={14} fill="currentColor" aria-hidden="true" />}>Join live</Btn>}
          {state === "tonight" && !s.recorded && <><Starts at={s.start} /><AddToCalendar session={s} variant="quiet" size="sm" label="Add to calendar" /></>}
          {state === "upcoming" && !s.recorded && <AddToCalendar session={s} variant="quiet" size="sm" label="Add to calendar" />}
          {state === "processing" && <span className="pj-proc">Replay up by morning. We'll let you know.</span>}
          {rep && <Btn variant={done ? "quiet" : "primary"} size="sm" to={P.session(s.id)} icon={<Play size={14} aria-hidden="true" />}>
            {done ? "Rewatch" : prog > 0 ? `Resume · ${fmtLeft(rep.length, s.progress)}` : "Watch"}
          </Btn>}
        </> : <>
          {/* Just the tier-named line here: the lock card above the list carries the Join button,
              so every row doesn't repeat it. */}
          {rep && <span className="pj-lockline"><Lock size={13} aria-hidden="true" />{lockLine("member", rep.length, true)}</span>}
          {state === "live" && <span className="pj-lockline"><Lock size={13} aria-hidden="true" />Live now · {PLANS.member.tag}</span>}
          {state === "processing" && <span className="pj-proc">Replay up by morning</span>}
          {(state === "upcoming" || state === "tonight") && <span className="pj-lockline"><Lock size={13} aria-hidden="true" />{s.recorded ? `Prep video · ${PLANS.member.tag}` : `Sew along live · ${PLANS.member.tag}`}</span>}
        </>}
      </div>

      {member && rep && rep.chapters.length > 1 && <div className="pj-chapters" aria-label={`${s.label} chapters`}>
        {rep.chapters.map(c => <PLink key={c.t} to={`${P.session(s.id)}?at=${c.t}`} className="pj-chap"><b>{fmtT(c.t)}</b>{c.label}</PLink>)}
      </div>}
    </div>
  </li>;
}

function Sessions({ p }: { p: Project }) {
  const { now, can, to } = usePortal();
  const nav = useNavigate();
  const ss = sessionsFor(p.id);
  const r = projectRuntime(p, now);
  const first = ss.find(x => x.replay && hasReplay(x, now) && !x.recorded) ?? ss.find(x => x.replay && hasReplay(x, now));
  const planned = ss.reduce((a, x) => a + (x.replay && hasReplay(x, now) ? x.replay.length : x.minutes * 60), 0);
  const pending = ss.some(x => !hasReplay(x, now));
  // While classes are still to come, quote the whole project (replays so far + planned), not just what's up.
  const kicker = pending
    ? `${ss.length} sessions · about ${fmtDur(planned)}${r.total ? ` · ${fmtDur(r.total)} up so far` : ""}`
    : `${ss.length} sessions · ${fmtDur(r.total)} of replays`;
  return <Section id="pj-classes" title="Classes" kicker={kicker}>
    {!can("replays") && <TierLock thing="replays" length={pending ? planned : r.total || undefined} preview={!!first}
      title={`Sew along with Chelsea${p.status === "past" ? "" : " live"}`}
      body={`Members get ${p.status === "past" ? "" : "both live classes and "}every replay of ${p.title}, with chapters and fix notes, plus the pattern code.`}
      onPreview={first ? () => nav(to(`${P.session(first.id)}?preview=1`)) : undefined} />}
    {ss.length === 0
      ? <Empty title="Class dates coming soon" />
      : <ol className="pj-sess-list">{ss.map(s => <SessionItem key={s.id} s={s} />)}</ol>}
  </Section>;
}

/* ═══════════════════════════ Makes ═══════════════════════════ */

function ShareMake({ open, onClose, onPost }: { open: boolean; onClose: () => void; onPost: (img: string, caption: string) => void }) {
  const [img, setImg] = useState<string | null>(null);
  const [cap, setCap] = useState("");
  const reset = () => { setImg(null); setCap(""); };
  return <Sheet open={open} onClose={() => { reset(); onClose(); }} title="Share your make"
    footer={<Btn block disabled={!img} onClick={() => { if (img) { onPost(img, cap.trim() || "My make!"); reset(); onClose(); } }}>Post to makes</Btn>}>
    <div className="pj-share">
      <label className="pj-drop">
        {img ? <img src={img} alt="Your photo" /> : <><ImagePlus size={28} aria-hidden="true" /><span>Choose a photo</span><small>Finished or in progress, both welcome</small></>}
        <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) setImg(URL.createObjectURL(f)); }} />
      </label>
      <label className="pj-field"><span>Caption</span><input value={cap} onChange={e => setCap(e.target.value)} placeholder="Gingham witch?? Gingham witch." maxLength={90} /></label>
      <p className="pj-hint">Prototype: your photo stays on this page and isn't uploaded.</p>
    </div>
  </Sheet>;
}

function Makes({ p }: { p: Project }) {
  const { now, can, role } = usePortal();
  const [mine, setMine] = useState<Make[]>([]);
  const [share, setShare] = useState(false);
  const all = [...mine, ...makesAt(now, p.id)];
  const action = can("makesPost")
    ? <Btn variant="secondary" size="sm" icon={<ImagePlus size={16} aria-hidden="true" />} onClick={() => setShare(true)} aria-haspopup="dialog">Share yours</Btn>
    : <TierLock thing="makesPost" variant="inline" />;
  return <Section id="pj-makes" title="Makes" kicker={all.length ? `${all.length} from the club` : undefined} action={action}>
    {all.length === 0
      ? <Empty title="No makes yet" hand="be the first!" body={p.status === "announced" ? "Makes show up here once the project starts." : "Share a photo of yours, finished or in progress."} />
      : <div className="pj-makes">
          {all.slice(0, 8).map((m, i) => <Polaroid key={m.id} src={m.img} alt={`${m.caption} by ${m.by === "me" ? "you" : m.by}`} caption={m.caption}
            by={<><Name person={m.by} badge={false} /> · {ago(m.at, now)}</>} tilt={TILTS[i % TILTS.length]} tape={TAPES[i % TAPES.length]} />)}
        </div>}
    {all.length > 0 && <PLink to={P.community} className="pj-more">See every make in Community</PLink>}
    {role !== "visitor" && <ShareMake open={share} onClose={() => setShare(false)}
      onPost={(img, caption) => setMine(m => [{ id: `local-${Date.now()}`, projectId: p.id, by: "me", img, caption, at: now, likes: 0 }, ...m])} />}
  </Section>;
}

/* ═══════════════════════════ Q&A ═══════════════════════════ */

const parseT = (text: string) => {
  const m = text.match(/\b(\d{1,2}):(\d{2}):(\d{2})\b|\b(\d{1,2}):(\d{2})\b/);
  if (!m) return undefined;
  return m[1] ? +m[1] * 3600 + +m[2] * 60 + +m[3] : +m[4] * 60 + +m[5];
};

function QItem({ q, voted, onVote }: { q: Question; voted: boolean; onVote: () => void }) {
  const { now, can } = usePortal();
  const x = getSession(q.sessionId);
  return <li className={`pj-q${q.answer ? " is-answered" : ""}`}>
    <div className="pj-q-side">
      <button type="button" className={`pj-vote${voted ? " is-on" : ""}`} onClick={onVote} aria-pressed={voted} aria-label={`Same question (${q.votes + (voted ? 1 : 0)})`} disabled={!can("communityPost")}>
        <ArrowBigUp size={20} aria-hidden="true" fill={voted ? "currentColor" : "none"} /><b>{q.votes + (voted ? 1 : 0)}</b>
      </button>
    </div>
    <div className="pj-q-main">
      <p className="pj-q-head"><Avatar person={q.by} size={24} /><Name person={q.by} badge={false} /><span>· {ago(q.at, now)}</span></p>
      <p className="pj-q-text">{q.text}</p>
      {x && q.t != null && (can("replays")
        ? <PLink to={`${P.session(x.id)}?at=${q.t}`} className="pj-ts"><Play size={12} fill="currentColor" aria-hidden="true" />{x.label} · {fmtT(q.t)}</PLink>
        : <span className="pj-ts is-off">{x.label} · {fmtT(q.t)}</span>)}
      {q.replies?.map((r, i) => <p key={i} className="pj-reply"><Name person={r.by} badge={false} /> {r.text}</p>)}
      {q.answer
        ? <div className="pj-a">
            <p className="pj-a-head"><Avatar person={CHELSEA} size={24} /><b>Chelsea</b><span className="pj-a-badge"><BadgeCheck size={14} aria-hidden="true" />Answer</span><span className="pj-a-ago">{ago(q.answer.at, now)}</span></p>
            <p>{q.answer.text}</p>
          </div>
        : <p className="pj-wait"><MessageCircle size={14} aria-hidden="true" />Waiting for Chelsea · usually within a day</p>}
    </div>
  </li>;
}

function QA({ p }: { p: Project }) {
  const { now, can } = usePortal();
  const [mine, setMine] = useState<Question[]>([]);
  const [votes, setVotes] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<"top" | "open">("top");
  const [text, setText] = useState("");
  const base = useMemo(() => [...mine, ...qaFor(p.id, now)], [mine, p.id, now]);
  const shown = sort === "open" ? base.filter(q => !q.answer) : base;
  const open = base.filter(q => !q.answer).length;
  const latest = sessionsFor(p.id).filter(x => x.replay && hasReplay(x, now) && !x.recorded).pop();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    const ts = parseT(t);
    setMine(m => [{ id: `local-${Date.now()}`, projectId: p.id, by: "me", at: now, text: t, votes: 0, sessionId: ts != null ? latest?.id : undefined, t: ts != null && latest ? ts : undefined }, ...m]);
    setText(""); setSort("top");
  };
  return <Section id="pj-qa" title="Questions & answers" kicker={base.length ? `${base.length} questions · ${open} waiting` : undefined}
    action={base.length > 0 && can("communityRead") ? <Segmented size="sm" value={sort} onChange={setSort} label="Sort questions" options={[{ value: "top", label: "Top" }, { value: "open", label: "Unanswered", count: open }]} /> : undefined}>
    {!can("communityRead")
      ? <TierLock thing="communityRead" force title="See what everyone's asking" body={`${base.length} questions on ${p.title}, with Chelsea's answers. Free with an account.`} />
      : <>
        {can("qaAsk")
          ? <form className="pj-ask" onSubmit={submit}>
              <Avatar person={ME} size={32} />
              <div className="pj-ask-main">
                <label htmlFor="pj-ask-t" className="pj-sr">Ask Chelsea a question</label>
                <textarea id="pj-ask-t" rows={2} value={text} onChange={e => setText(e.target.value)} placeholder={`Ask about ${p.title}… add a time like 1:32:00 to link the replay`} />
                <div className="pj-ask-foot"><small>Chelsea answers most questions within a day.</small><Btn type="submit" size="sm" disabled={!text.trim()}>Ask</Btn></div>
              </div>
            </form>
          : <div className="pj-ask is-locked"><Lock size={16} aria-hidden="true" /><span>Members can ask Chelsea questions on any class.</span><TierLock thing="qaAsk" variant="inline" /></div>}
        {shown.length === 0
          ? <Empty title={sort === "open" ? "Everything's answered" : "No questions yet"} hand={sort === "open" ? "yay!" : "ask away!"} />
          : <ul className="pj-qs">{shown.map(q => <QItem key={q.id} q={q} voted={votes.has(q.id)} onVote={() => setVotes(v => { const n = new Set(v); if (n.has(q.id)) n.delete(q.id); else n.add(q.id); return n; })} />)}</ul>}
      </>}
  </Section>;
}

/* ═══════════════════════════ Rail: next session ═══════════════════════════ */

function NextCard({ p }: { p: Project }) {
  const { now, tz, can } = usePortal();
  const st = projectStatus(p, now, tz);
  const pick = nextToWatch(p, now);
  const member = can("live");
  const s = st.session;
  if (s && (st.state === "live" || st.state === "tonight" || st.state === "upcoming" || st.state === "processing")) {
    return <Card className="pj-next" tone={st.state === "live" ? "ink" : "plain"}>
      <p className="pt-kicker">{st.state === "live" ? "Happening now" : st.state === "processing" ? "Just finished" : "Next class"}</p>
      <p className="pj-next-t">{p.title} · {s.label}</p>
      <LocalTime date={s.start} relative className="pj-next-time" />
      {(st.state === "tonight" || st.state === "upcoming") && <CountdownBoxes to={s.start} />}
      {st.state === "processing" && <p className="pj-proc">The replay will be up by morning, with chapters.</p>}
      {member
        ? <div className="pj-next-act">
            {st.state === "live" && <Btn variant="live" block to={P.session(s.id)} icon={<Play size={16} fill="currentColor" aria-hidden="true" />}>Join live</Btn>}
            {(st.state === "tonight" || st.state === "upcoming") && <AddToCalendar session={s} block />}
          </div>
        : <div className="pj-next-act"><p className="pj-next-lock"><Lock size={14} aria-hidden="true" /> Live classes: {PLANS.member.tag}</p><JoinButton need="member" block onDark={st.state === "live"} variant={st.state === "live" ? "pop" : "primary"} /></div>}
    </Card>;
  }
  if (pick && can("replays")) {
    const w = watchLabel(pick), x = pick.session;
    return <Card className="pj-next">
      <p className="pt-kicker">{pick.kind === "resume" ? "Pick up where you left off" : pick.kind === "start" ? "Up next" : "All watched"}</p>
      <PLink to={P.session(x.id)} className="pj-next-thumb"><Thumb src={x.replay!.poster} progress={(x.progress ?? 0) / x.replay!.length} play duration={x.replay!.length} /></PLink>
      <p className="pj-next-t">{p.title} · {x.label}</p>
      <Btn block to={P.session(x.id)} icon={<Play size={16} aria-hidden="true" />}>{w.label} · {w.meta}</Btn>
    </Card>;
  }
  return null;
}

/* ═══════════════════════════ Screen ═══════════════════════════ */

export function PortalProject() {
  const { id } = useParams();
  const p = getProject(id);
  const wide = useWide();
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = window.setTimeout(() => document.getElementById(`pj-${hash.slice(1)}`)?.scrollIntoView({ block: "start" }), 120);
    return () => window.clearTimeout(t);
  }, [hash, wide]);

  if (!p) return <PortalShell active="classes" back={{ to: P.classes, label: "Classes" }} title="Project not found">
    <Empty title="We couldn't find that project" body="It may have moved. Every project is on the Classes tab." action={<Btn to={P.classes}>All projects</Btn>} />
  </PortalShell>;

  const rail = wide ? <>
    <NextCard p={p} />
    <div id="pj-supplies" className="pj-rail-need"><Supplies p={p} compact /></div>
  </> : undefined;

  return <PortalShell active="classes" back={{ to: P.classes, label: "Classes" }} rail={rail} className="pj-root">
    <Hero p={p} />
    <ChipBar wide={wide} />
    <Overview p={p} />
    {!wide && <div id="pj-supplies" className="pj-sec"><Supplies p={p} /></div>}
    <Sessions p={p} />
    <Makes p={p} />
    <QA p={p} />
  </PortalShell>;
}
